import type { ReactElement } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { Box } from "@chakra-ui/react";
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from "next";
import ErrorPage from "pages/404";
import AppLayout from "components/app/layout/app-layout";
import InvestorPageLayout from "components/investor/investor-page-layout/investor-page-layout";
import { getFirst } from "utils/array";
import fetcher from "utils/fetcher";
import type { InvestorInitialDataResponse } from "types/api/investor";

const InvestorOverview = dynamic(
  () => import("components/investor/investor-overview/investor-overview"),
  { ssr: false }
);

interface InvestorStaticProps {
  investor?: InvestorInitialDataResponse;
  errorCode?: number;
}

export const getStaticProps: GetStaticProps<InvestorStaticProps> = async ({
  params: { slug },
}) => {
  const investorSlug = slug;

  try {
    const investor = await fetcher<InvestorInitialDataResponse>(
      `/system/investor/${investorSlug}`
    );

    return { props: { investor }, revalidate: 500 };
  } catch (error) {
    return {
      props: { errorCode: 404 },
      revalidate: 60,
    };
  }
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: "blocking",
});

const InvestorPage = ({
  investor,
  errorCode = null,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const router = useRouter();

  const investorSlug = getFirst(router.query.slug);

  if (errorCode) {
    return <ErrorPage />;
  }

  if (investor) {
    return (
      <InvestorPageLayout investor={investor} slug={investorSlug}>
        <Box bg="slate.100" flex={1}>
          <InvestorOverview investorSlug={investorSlug} isInternational={false} />
        </Box>
      </InvestorPageLayout>
    );
  }
  return null;
};

InvestorPage.getLayout = (page: ReactElement) => <AppLayout>{page}</AppLayout>;

export default InvestorPage;
